import {
  hexToHSL,
  hslToCSS,
  shouldDisplayToastVersion,
  updateLastToastDisplay,
} from "./../helper/helper";
import { useQuery } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { systemActions } from "@/redux/system/system-slice";
import type { DataAwareResponse, SystemRole, SystemSettings, User } from "@/types/types";
import { msalInstance, graphRequest, graphConfig } from "@/authConfig";
import { axiosInstance } from "@/helper/axios-instance";
import errorNotifications, { compareBackendVersions } from "@/helper/helper";
import { useIsAuthenticated } from "@azure/msal-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { getErrorMessage } from "@/helper/errorMessages";

let isError = false;

export function useUser() {
  const account = msalInstance?.getActiveAccount();
  const dispatch = useDispatch();
  const isAuthed = useIsAuthenticated();

  return useQuery({
    queryKey: ["user", account?.idToken, isAuthed],
    queryFn: async () => {
      const systemSettings = await axiosInstance({
        method: "GET",
        url: `Organization/CurrentOrg`,
      })
        .then((response: AxiosResponse<DataAwareResponse<SystemSettings>>) => {
          if (response.data.hasError) {
            errorNotifications(response.data.validation.items);
            return;
          }

          return response.data.data;
        })
        .catch((e: unknown) => {
          getErrorMessage(e);
        });

      if (isError || !systemSettings?.user) return;

      const colorScheme = systemSettings?.branding?.colorScheme;
      const logo = systemSettings?.branding?.logo;

      if (colorScheme) {
        const { primaryColor, secondaryColor } = colorScheme;

        const primaryHSL = hexToHSL(primaryColor);
        const secondaryHSL = hexToHSL(secondaryColor);

        const primaryHSLString = `${primaryHSL.h} ${primaryHSL.s}% ${primaryHSL.l}%`;
        const secondaryHSLString = `${secondaryHSL.h} ${secondaryHSL.s}% ${secondaryHSL.l}%`;

        document.documentElement.style.setProperty("--primary-color", primaryHSLString);
        document.documentElement.style.setProperty("--secondary-color", secondaryHSLString);

        const tertiaryLightness = Math.min(primaryHSL.l + 15, 100);
        const tertiaryHSLString = `${primaryHSL.h} ${primaryHSL.s}% ${tertiaryLightness}%`;

        document.documentElement.style.setProperty("--tertiary-color", tertiaryHSLString);

        dispatch(systemActions.setColorScheme(colorScheme));
        dispatch(systemActions.setLogo(logo));
      }

      const backendCompatibilityStatus = compareBackendVersions(systemSettings.version);

      if (!backendCompatibilityStatus.canRun) {
        if (shouldDisplayToastVersion()) {
          toast.error(backendCompatibilityStatus.message, {
            toastId: "version-error",
            autoClose: false,
          });
          updateLastToastDisplay();
        }
        return;
      }

      if (backendCompatibilityStatus.warn) {
        if (shouldDisplayToastVersion()) {
          toast.warn(backendCompatibilityStatus.message, {
            toastId: "version-warn",
            autoClose: false,
          });
          updateLastToastDisplay();
        }
      }

      dispatch(systemActions.setVersion(systemSettings.version));

      const roles = (systemSettings?.user?.roles as SystemRole[]).map(
        (role: SystemRole) => role.name
      );

      const userData: User = {
        ...systemSettings.user!,
        name: account?.name,
        email: account?.username,
        roles: roles,
        photo: "",
      };

      Sentry.setUser({
        email: systemSettings.user!.email,
        id: systemSettings.user!.id,
        username: systemSettings.user!.username,
      });

      const tokenResp = await msalInstance.acquireTokenSilent({
        ...graphRequest,
        account: account!,
      });

      const headers = new Headers();
      headers.append("Authorization", `Bearer ${tokenResp?.accessToken}`);
      headers.append("Content-Type", "image/jpeg");
      headers.append("Accept", "image/jpeg");

      const options = {
        method: "GET",
        headers: headers,
      };

      const photoResponse = await fetch(graphConfig.graphMePhotoEndpoint, options);

      if (!photoResponse.ok) {
        console.error("HTTP error! status: ", photoResponse.status);
      } else {
        const data = await photoResponse.blob();
        if (data !== null) {
          window.URL = window.URL || window.webkitURL;
          userData.photo = window.URL.createObjectURL(data);
          dispatch(systemActions.setUser(userData));

          return userData;
        }
      }
      dispatch(systemActions.setUser(userData));

      return userData;
    },
  });
}
